import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useFormik } from 'formik'
import PasswordMeter from 'Presentation/Components/PasswordMeter'
import { changePassword } from '../core/_requests'
// import { useAuthStore } from 'Store/Auth'
import { useAuthSessionStore } from 'Store/AuthSession'
import { useCookies } from 'react-cookie'
import Swal from 'sweetalert2'

const initialValues = {
	password: '',
	confirmPassword: '',
}

const changePasswordSchema = Yup.object().shape({
	password: Yup.string()
		.min(8, 'Minimum 8 symbols')
		.max(50, 'Maximum 50 symbols')
		.required('Password is required'),
	confirmPassword: Yup.string()
		.min(8, 'Minimum 8 symbols')
		.max(50, 'Maximum 50 symbols')
		// @ts-ignore
		.oneOf([Yup.ref('password'), null], 'Passwords must match')
		.required('Confirm Password is required'),
})

export function ChangePassword() {
	const navigate = useNavigate()
	const [cookies] = useCookies(['kumopack-supplier-access-token'])
	const accessToken = cookies['kumopack-supplier-access-token']
	const sessionAccessToken = useAuthSessionStore(state => state.accessToken)
	const [searchParams] = useSearchParams()
	const token = searchParams.get('token')
	const [loading, setLoading] = useState(false)
	const [showPassword, setShowPassword] = useState<boolean>(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
	const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
	const formik = useFormik({
		initialValues,
		validationSchema: changePasswordSchema,
		onSubmit: (values, { setStatus, setSubmitting }) => {
			setLoading(true)
			setHasErrors(undefined)
			setTimeout(() => {
				if (token) {
					changePassword(token, values.password)
						.then(({ data: { result } }) => {
							setHasErrors(false)
							setLoading(false)
						})
						.catch((err: any) => {
							setHasErrors(true)
							setLoading(false)
							setSubmitting(false)
							setStatus(
								err.response.data.message ??
									'Sorry, looks like there are some errors detected, please try again.'
							)
						})
				}
			}, 1000)
		},
	})

	useEffect(() => {
		if (!token && (accessToken || sessionAccessToken)) {
			Swal.fire({
				title: 'Not allow to change password',
				text: 'Invalid token',
				icon: 'error',
				confirmButtonText: 'Confirm',
			}).then(() => {
				navigate('/auth/login')
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, accessToken, sessionAccessToken])

	return (
		<form
			className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
			noValidate
			id="kt_login_password_reset_form"
			onSubmit={formik.handleSubmit}>
			<div className="text-center mb-10">
				{/* begin::Title */}
				<h1 className="text-dark fw-bolder mb-3">Change your password</h1>
				{/* end::Title */}

				{/* begin::Link */}
				<div className="text-gray-500 fw-semibold fs-6">
					Ensure your password is a minimum of 8 characters long, includes at least 1 uppercase and
					1 lowercase letter, 1 special character, and 1 number for enhanced security.
				</div>
				{/* end::Link */}
			</div>

			{/* begin::Title */}
			{hasErrors === true && (
				<div className="mb-lg-15 alert alert-danger">
					<div className="alert-text font-weight-bold">{formik.status}</div>
				</div>
			)}

			{hasErrors === false && (
				<div className="mb-10 bg-light-info p-8 rounded">
					<div className="text-info">Change password successfully.</div>
				</div>
			)}
			{/* end::Title */}

			<div className="fv-row mb-8">
				<label className="form-label fw-bolder text-gray-900 fs-6">Password</label>
				<div className="position-relative">
					<input
						type={showPassword ? 'text' : 'password'}
						placeholder=""
						autoComplete="off"
						{...formik.getFieldProps('password')}
						style={{ backgroundImage: 'none' }}
						className={clsx(
							'form-control bg-transparent',
							{ 'is-invalid': formik.touched.password && formik.errors.password },
							{
								'is-valid': formik.touched.password && !formik.errors.password,
							}
						)}
					/>
					<span
						className="btn btn-sm btn-icon btn-active-secondary position-absolute translate-middle top-50 end-0 me-n2"
						onClick={() => setShowPassword(prevState => !prevState)}>
						<img
							src={
								showPassword
									? '/media/icons/kumopack/eye-grey.svg'
									: '/media/icons/kumopack/eye-off-grey.svg'
							}
							alt="Eye Off Grey"
							width={17}
							style={{ userSelect: 'none', pointerEvents: 'none' }}
						/>
					</span>
				</div>

				<PasswordMeter minStrength={8} password={formik.values.password} thresholdLength={8} />

				{formik.touched.password && formik.errors.password && (
					<div className="fv-plugins-message-container">
						<div className="fv-help-block">
							<span role="alert">{formik.errors.password}</span>
						</div>
					</div>
				)}
			</div>

			<div className="fv-row mb-8">
				<label className="form-label fw-bolder text-gray-900 fs-6">Confirm Password</label>
				<div className="position-relative">
					<input
						type={showConfirmPassword ? 'text' : 'password'}
						placeholder=""
						autoComplete="off"
						{...formik.getFieldProps('confirmPassword')}
						style={{ backgroundImage: 'none' }}
						className={clsx(
							'form-control l bg-transparent',
							{ 'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword },
							{
								'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
							}
						)}
					/>
					<span
						className="btn btn-sm btn-icon btn-active-secondary position-absolute translate-middle top-50 end-0 me-n2"
						onClick={() => setShowConfirmPassword(prevState => !prevState)}>
						<img
							src={
								showConfirmPassword
									? '/media/icons/kumopack/eye-grey.svg'
									: '/media/icons/kumopack/eye-off-grey.svg'
							}
							alt="Eye Off Grey"
							width={17}
							style={{ userSelect: 'none', pointerEvents: 'none' }}
						/>
					</span>
					<PasswordMeter
						minStrength={8}
						password={formik.values.confirmPassword}
						thresholdLength={8}
					/>
				</div>

				{formik.touched.confirmPassword && formik.errors.confirmPassword && (
					<div className="fv-plugins-message-container">
						<div className="fv-help-block">
							<span role="alert">{formik.errors.confirmPassword}</span>
						</div>
					</div>
				)}
			</div>

			{/* begin::Form group */}
			<div className="d-flex flex-wrap justify-content-center pb-lg-0">
				<button
					type="submit"
					id="kt_password_reset_submit"
					className="btn btn-lg btn-kumopack-primary-700 text-kumopack-base-white me-4">
					<span className="indicator-label">Submit</span>
					{loading && (
						<span className="indicator-progress">
							Please wait...
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					)}
				</button>
				<Link to="/auth/login">
					<button
						type="button"
						id="kt_login_password_reset_form_cancel_button"
						className="btn btn-lg btn-light"
						disabled={formik.isSubmitting || !formik.isValid}>
						Cancel
					</button>
				</Link>{' '}
			</div>
			{/* end::Form group */}
		</form>
	)
}
